import { api, unwrap } from "@lumberset/api-client";
import { type StaffTicketFilters, supportQueries } from "@lumberset/support-ui";
import { queryOptions } from "@tanstack/react-query";

/** Query definitions, one per thing the Staff panel reads. Keys are what mutations invalidate. */

export const meQuery = queryOptions({
  queryKey: ["me"],
  queryFn: () => unwrap(api.ops.me.$get()),
  staleTime: 60_000,
});

/**
 * Open is what the queue shows by default: pending and in progress Reviews.
 * Prepared is the open ones prepared for final review from the operator
 * command line.
 */
export type ReviewFilter = "open" | "prepared" | "approved";

const reviewFilters = {
  open: {},
  prepared: { prepared: "true" },
  approved: { status: "approved" },
} as const;

export const reviewsQuery = (filter: ReviewFilter) =>
  queryOptions({
    queryKey: ["reviews", { filter }],
    queryFn: () => unwrap(api.ops.reviews.$get({ query: reviewFilters[filter] })),
  });

export const reviewQuery = (id: string) =>
  queryOptions({
    queryKey: ["reviews", id],
    queryFn: () => unwrap(api.ops.reviews[":id"].$get({ param: { id } })),
  });

/** Takeoffs whose reading failed or stopped at the limit, that can be read again. */
export const stoppedRunsQuery = queryOptions({
  queryKey: ["takeoffs", "stopped"],
  queryFn: () => unwrap(api.ops.takeoffs.stopped.$get()),
});

/** Sends a takeoff that failed or stopped at the limit back to be read again. */
export const readAgainRun = (runId: string) =>
  unwrap(api.ops.takeoffs[":id"]["read-again"].$post({ param: { id: runId } }));

export const yardsQuery = (q?: string) =>
  queryOptions({
    queryKey: ["yards", { q: q ?? "" }],
    queryFn: () => unwrap(api.ops.yards.$get({ query: { q } })),
    placeholderData: (previous) => previous,
  });

export const yardQuery = (id: string) =>
  queryOptions({
    queryKey: ["yards", id],
    queryFn: () => unwrap(api.ops.yards[":id"].$get({ param: { id } })),
  });

export type ReviewDetail = Awaited<
  ReturnType<NonNullable<ReturnType<typeof reviewQuery>["queryFn"]>>
>;

/** Support: the numbers on the queue, the queue itself, and one Ticket. Keys are shared with the support screens. */
export const supportStatsQuery = supportQueries.stats(api);

export const supportTicketsQuery = (filters: StaffTicketFilters) =>
  supportQueries.staffTickets(filters, api);

export const supportTicketQuery = (id: string) => supportQueries.staffTicket(id, api);

/** The platform role of a signed-in Staff member, as the sidebar shows it. */
export const roleLabels: Record<string, string> = {
  reviewer: "Reviewer",
  support: "Support",
  admin: "Admin",
};

/** Reading accuracy: the tiles, recent runs, one run, and the harness scores. */
export const accuracyOverviewQuery = (days: number) =>
  queryOptions({
    queryKey: ["accuracy", "overview", days],
    queryFn: () => unwrap(api.staff.accuracy.overview.$get({ query: { days: String(days) } })),
  });

export const accuracyRunsQuery = (days: number) =>
  queryOptions({
    queryKey: ["accuracy", "runs", { days }],
    queryFn: () => unwrap(api.staff.accuracy.runs.$get({ query: { days: String(days) } })),
  });

export const accuracyRunQuery = (id: string) =>
  queryOptions({
    queryKey: ["accuracy", "runs", id],
    queryFn: () => unwrap(api.staff.accuracy.runs[":id"].$get({ param: { id } })),
  });

export const accuracyScoresQuery = queryOptions({
  queryKey: ["accuracy", "scores"],
  queryFn: () => unwrap(api.staff.accuracy.scores.$get()),
});

/** Whether Confidence works: reliability against Review, and Corrections found early. */
export const accuracyConfidenceQuery = (days: number) =>
  queryOptions({
    queryKey: ["accuracy", "confidence", days],
    queryFn: () => unwrap(api.staff.accuracy.confidence.$get({ query: { days: String(days) } })),
  });
